import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  PasswordInput,
  Stack,
  Text,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { klaviyo, type KlaviyoStatus } from "./api";
import styles from "../catalogue/Catalogue.module.scss";

/**
 * The Klaviyo integration (`R-ANL-10`): a private key, and whether one is set.
 *
 * Its own card, and its own save, rather than a field in the settings form above: the key is
 * checked against Klaviyo before it is stored, which can take a second and can be refused, and a
 * refusal should not hold up a change to the shipping fee. The key is never shown again once
 * saved — the card says which account it opens and how it ends, and replacing it means pasting a
 * new one.
 */
export function KlaviyoSettings() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<KlaviyoStatus | undefined>(undefined);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState<"save" | "remove" | undefined>(undefined);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);

  useEffect(() => {
    klaviyo
      .read()
      .then((answer) => setStatus(answer.klaviyo))
      .catch((failure: unknown) =>
        setError(
          failure instanceof ApiError ? failure.message : t("shows.unreachable"),
        ),
      );
  }, [t]);

  async function act(action: "save" | "remove") {
    setBusy(action);
    setError(undefined);
    setNote(undefined);
    try {
      const answer =
        action === "save" ? await klaviyo.save(apiKey.trim()) : await klaviyo.remove();
      setStatus(answer.klaviyo);
      setApiKey("");
      setConfirming(false);
      setNote(
        t(action === "save" ? "settings.klaviyoSaved" : "settings.klaviyoRemoved"),
      );
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t("shows.unreachable"));
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <Paper withBorder radius="lg" p="lg" className={styles.card}>
      <Stack gap="sm">
        <Text fw={600}>{t("settings.klaviyoHeading")}</Text>
        <Text size="sm" c="dimmed">
          {t("settings.klaviyoHelp")}
        </Text>

        {!status ? (
          error ? null : (
            <Loader size="sm" color="ink" />
          )
        ) : (
          <Text size="sm">
            {status.configured
              ? t("settings.klaviyoConnected", {
                  account: status.account ?? t("settings.klaviyoUnnamed"),
                  hint: status.hint ?? "",
                })
              : t("settings.klaviyoOff")}
          </Text>
        )}

        <Group gap="md" align="flex-end" wrap="wrap">
          <PasswordInput
            label={t(
              status?.configured
                ? "settings.klaviyoReplaceLabel"
                : "settings.klaviyoKeyLabel",
            )}
            description={t("settings.klaviyoKeyHelp")}
            value={apiKey}
            placeholder="pk_…"
            autoComplete="off"
            onChange={(event) => setApiKey(event.currentTarget.value)}
            disabled={busy !== undefined}
            miw={280}
          />
          <Button
            onClick={() => void act("save")}
            loading={busy === "save"}
            disabled={apiKey.trim() === "" || busy !== undefined}
          >
            {t("settings.klaviyoSave")}
          </Button>
          {status?.configured && !confirming ? (
            <Button
              variant="default"
              onClick={() => setConfirming(true)}
              disabled={busy !== undefined}
            >
              {t("settings.klaviyoRemove")}
            </Button>
          ) : null}
        </Group>

        {confirming ? (
          <Group gap="xs" wrap="nowrap" preventGrowOverflow={false}>
            <Text size="xs" c="dimmed" maw="60%">
              {t("settings.klaviyoConfirmRemove")}
            </Text>
            <Button
              size="compact-sm"
              color="red"
              flex="none"
              loading={busy === "remove"}
              onClick={() => void act("remove")}
            >
              {t("shows.confirmYes")}
            </Button>
            <Button
              size="compact-sm"
              variant="default"
              flex="none"
              onClick={() => setConfirming(false)}
            >
              {t("shows.confirmNo")}
            </Button>
          </Group>
        ) : null}

        {note ? (
          <Text size="xs" c="green">
            {note}
          </Text>
        ) : null}
        {error ? (
          <Alert color="red" variant="light" radius="md">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
